import { useId, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getExtraction } from "../../../api/knowledgeData/knowledgeDataApi";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { HumanStepRequestedPayload, ThreadEntry } from "../../../api/threads/liveMessages";
import { readEntries } from "../../../api/threads/threadsApi";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { decidedResultOf, responseDefinition, reviewPartOf } from "../../threads/entries/humanStepResponse";
import { namedPerson } from "../../threads/data/namedPerson";
import { useMemberNames } from "../../threads/data/useMemberNames";
import LineageNotice from "../extractions/LineageNotice";
import AcceptVitaStep from "./AcceptVitaStep";
import ResultDecision from "./ResultDecision";
import { recordedDecision } from "./resultChoice";
import ResultView from "./ResultView";

interface AcceptResultStepProps {
  readonly threadId: string;
  readonly entry: ThreadEntry;
  readonly payload: HumanStepRequestedPayload;
}

/**
 * The answer form of "Accept the knowledge data" (ADR-0026 slice 3a, K-1): the result in its
 * one view, the decisions made on it, and, for a curator of its label list or of the
 * project's knowledge data, Accept (Merge, for a correction) or Reject with a reason. The
 * server decides who may; this is the only place a result is decided. An assembled vita is
 * decided the same way, all its parts with one answer (E4, `AcceptVitaStep`).
 */
export default function AcceptResultStep({ threadId, entry, payload }: AcceptResultStepProps) {
  const { t } = useTranslation();
  const decided = useMemo(() => decidedResultOf(payload), [payload]);
  // The common review part's comment rule (proposal 0.8, O1): with it, every decision carries a comment.
  const commentRequired = reviewPartOf(responseDefinition(payload.response))?.commentRequired === true;
  const load = useCallback(async (signal: AbortSignal) => {
    if (decided === null || "assembly" in decided) return null;
    return getExtraction("key" in decided ? decided.key : await resultOfEntry(threadId, decided.entry, signal), signal);
  }, [decided, threadId]);
  const { state, reload } = useLoad(load);
  if (decided === null) return <p className={FORM_HELP_TEXT_CLASS}>{t("data.review.noResult")}</p>;
  if ("assembly" in decided) return <AcceptVitaStep threadId={threadId} entry={entry} assembly={decided.assembly} commentRequired={commentRequired} />;
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  return state.value === null ? null : <AcceptResultForm threadId={threadId} entry={entry} extraction={state.value} commentRequired={commentRequired} />;
}

function AcceptResultForm({ threadId, entry, extraction, commentRequired }: { threadId: string; entry: ThreadEntry; extraction: Extraction; commentRequired: boolean }) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const people = useMemberNames(extraction.projectId);
  return (
    <div className="space-y-3">
      <LineageNotice extraction={extraction} />
      <ResultView extraction={extraction} />
      <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
        <h3 id={headingId} className="app-title text-base font-semibold">{t("data.curation.title")}</h3>
        {extraction.decisions.length === 0 ? <EmptyState>{t("data.curation.none")}</EmptyState> : null}
        <ul className="space-y-1 text-sm">
          {extraction.decisions.map((decision) => (
            <li key={decision.decisionId}>
              <span className="font-semibold">{t(`data.curation.kinds.${decision.kind}`)}</span>{" "}
              {t("data.curation.by", { ...namedPerson(people, decision.curator), time: formatTimeAgo(decision.at, i18n.resolvedLanguage) })}
              {decision.comment !== "" ? <span> — {decision.comment}</span> : null}
            </li>
          ))}
        </ul>
        <ResultDecision
          threadId={threadId}
          entry={entry}
          canDecide={extraction.canDecide}
          acceptable={true}
          merge={extraction.labelList === null ? { newerCount: extraction.lineage.pending.filter((pending) => pending.newer).length } : null}
          notCurator={extraction.state === "proposed" ? t(extraction.labelList === null ? "data.lineage.notCurator" : "data.curation.notCurator") : null}
          recorded={recordedDecision(extraction)}
          commentRequired={commentRequired}
        />
      </section>
    </div>
  );
}

/** The result recorded at a path entry: of the Thread that entry belongs to, a fork's parent's included. */
async function resultOfEntry(threadId: string, number: number, signal: AbortSignal): Promise<string> {
  const page = await readEntries(threadId, number - 1, 1, signal);
  return `${page.entries[0]?.thread_id ?? threadId}#${String(number)}`;
}
